import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, ApiError, type MailStatus } from '../lib/api';
import { dateTime, relativeTime } from '../lib/format';

// Configuración → Correo: conectar el buzón de soporte (Google), qué avisos salen y los
// dominios de correo de cada cliente (para reconocer correos nuevos).

const btnPrimary = 'inline-flex h-9 items-center rounded-md bg-accent px-4 text-sm font-semibold text-white hover:bg-accent-dark disabled:opacity-50';
const btnSecondary = 'inline-flex h-9 items-center rounded-md border border-[#D6D3CD] bg-white px-3 text-sm text-[#3F3D39] hover:bg-paper disabled:opacity-50';
const errorText = (e: unknown) => (e instanceof ApiError ? e.message : 'Algo salió mal. Inténtalo de nuevo.');

const OUTCOME: Record<string, [string, string]> = {
  message: ['Entró al ticket', 'text-accent-dark'],
  note: ['Nota de un agente', 'text-accent-dark'],
  ticket: ['Creó el ticket', 'text-accent-dark'],
  triage: ['Por clasificar', 'text-[#9A3412]'],
  auto_reply: ['Ignorado: respuesta automática', 'text-muted'],
  own: ['Ignorado: enviado por el buzón', 'text-muted'],
  error: ['Error (se reintenta)', 'text-[#8F1D1D]'],
  retry: ['Reintentando', 'text-[#9A3412]'],
  pending: ['Procesando', 'text-muted']
};

const NOTIFY_LABEL: Record<'ack' | 'reply' | 'resolved' | 'agent' | 'sla', [string, string]> = {
  ack: ['Acuse al crear el ticket', 'El solicitante recibe su número de ticket.'],
  reply: ['Respuestas al cliente', 'Lo que el agente responde sale por correo, en el mismo hilo.'],
  resolved: ['Aviso de resuelto', 'Con la solución y cómo reabrir si algo sigue mal.'],
  agent: ['Avisos al equipo', 'Al agente cuando se le asigna un ticket o el cliente responde.'],
  sla: ['Avisos de SLA', 'Al 75 % al responsable; al vencer, también a los líderes del grupo.']
};

function DomainRow({ org, onSaved }: { org: MailStatus['organizations'][number]; onSaved: () => void }) {
  const [value, setValue] = useState(org.domains.join(', '));
  const [error, setError] = useState('');
  useEffect(() => setValue(org.domains.join(', ')), [org.domains]);
  const save = useMutation({
    mutationFn: () => api('/admin/org-domains', {
      method: 'PUT',
      body: JSON.stringify({ organizationId: org.id, domains: value.split(/[\s,;]+/).map(d => d.trim()).filter(Boolean) })
    }),
    onSuccess: () => { setError(''); onSaved(); },
    onError: e => setError(errorText(e))
  });
  const dirty = value !== org.domains.join(', ');
  return (
    <tr className="border-b border-[#EFEDE9] last:border-0">
      <td className="px-3 py-2 font-medium">{org.name}</td>
      <td className="px-3 py-2">
        <input aria-label={`Dominios de ${org.name}`} value={value} onChange={e => setValue(e.target.value)} placeholder="ej. cliente.com"
          className="h-9 w-full rounded-md border border-[#D6D3CD] bg-white px-2.5 font-mono text-xs focus:border-accent focus:outline-none" />
        {error && <p role="alert" className="mt-1 text-xs text-[#8F1D1D]">{error}</p>}
      </td>
      <td className="px-3 py-2 text-right">
        <button type="button" className={btnSecondary} disabled={!dirty || save.isPending} onClick={() => save.mutate()}>Guardar</button>
      </td>
    </tr>
  );
}

export function MailSettings({ flash }: { flash: { kind: 'ok' | 'error' | 'cancelado'; text: string } | null }) {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['admin-mail'], queryFn: () => api<MailStatus>('/admin/mail') });
  const [error, setError] = useState('');
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['admin-mail'] });

  const connect = useMutation({
    mutationFn: () => api<{ url: string }>('/admin/mail/connect', { method: 'POST', body: JSON.stringify({}) }),
    onSuccess: r => { window.location.assign(r.url); },
    onError: e => setError(errorText(e))
  });
  const disconnect = useMutation({
    mutationFn: () => api('/admin/mail', { method: 'DELETE' }),
    onSuccess: refresh,
    onError: e => setError(errorText(e))
  });
  const notify = useMutation({
    mutationFn: (n: NonNullable<MailStatus['mailbox']>['notify']) => api('/admin/mail/notify', { method: 'PUT', body: JSON.stringify(n) }),
    onSuccess: refresh,
    onError: e => setError(errorText(e))
  });

  const data = query.data;
  const box = data?.mailbox;

  return (
    <div className="flex flex-col gap-5">
      {flash && (
        <p role="status" className={`rounded-md px-4 py-2 text-sm ${flash.kind === 'ok' ? 'bg-accent-soft text-accent-dark' : 'bg-[#FBEAEA] text-[#8F1D1D]'}`}>{flash.text}</p>
      )}
      {error && <p role="alert" className="rounded-md bg-[#FBEAEA] px-4 py-2 text-sm text-[#8F1D1D]">{error}</p>}

      <section className="rounded-lg border border-line bg-white p-5">
        <h2 className="text-base font-semibold">Buzón de soporte</h2>
        {query.isLoading && <p role="status" className="mt-2 text-sm text-muted">Cargando…</p>}
        {data && !data.available && (
          <p className="mt-2 text-sm text-muted">El servidor todavía no tiene configurada la conexión con Google (pasos de la sección 6 de la guía de despliegue).</p>
        )}
        {data?.available && !box && (
          <div className="mt-2 flex flex-col gap-3">
            <p className="text-sm text-muted">
              Conecta la cuenta de Google del buzón de soporte. Veta Desk leerá los correos que lleguen a su bandeja de entrada
              (los marca con la etiqueta <span className="font-mono text-xs">Veta Desk/Procesado</span>) y enviará los avisos desde esa dirección.
              Inicia sesión con la cuenta del buzón, no con la tuya.
            </p>
            <div><button type="button" className={btnPrimary} disabled={connect.isPending} onClick={() => connect.mutate()}>Conectar buzón de Google</button></div>
          </div>
        )}
        {box && (
          <div className="mt-3 flex flex-col gap-3 text-sm">
            <div className="flex flex-wrap items-center gap-3">
              <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${box.status === 'connected' ? 'bg-accent-soft text-accent-dark' : 'bg-[#FBEAEA] text-[#8F1D1D]'}`}>
                <span className={`h-2 w-2 rounded-full ${box.status === 'connected' ? 'bg-accent' : 'bg-[#B91C1C]'}`} />
                {box.status === 'connected' ? 'Conectado' : 'Requiere reconectar'}
              </span>
              <span className="font-mono">{box.address}</span>
            </div>
            <p className="text-xs text-muted">
              Conectado por {box.connectedBy?.name} · {dateTime(box.connectedAt)} · última lectura {box.lastPollAt ? relativeTime(box.lastPollAt) : 'pendiente'}
            </p>
            {box.lastError && <p className="rounded-md bg-[#FBEAEA] px-3 py-2 text-xs text-[#8F1D1D]">Último error: {box.lastError}</p>}
            <div className="flex gap-2">
              <button type="button" className={btnSecondary} disabled={connect.isPending} onClick={() => connect.mutate()}>Reconectar</button>
              <button type="button" className={btnSecondary} disabled={disconnect.isPending}
                onClick={() => { if (window.confirm('¿Desconectar el buzón? Dejarán de entrar y salir correos.')) disconnect.mutate(); }}>
                Desconectar
              </button>
            </div>
          </div>
        )}
      </section>

      {box && (
        <section className="rounded-lg border border-line bg-white p-5">
          <h2 className="text-base font-semibold">Avisos por correo</h2>
          <ul className="mt-3 flex flex-col gap-3">
            {(Object.keys(NOTIFY_LABEL) as (keyof typeof NOTIFY_LABEL)[]).map(k => (
              <li key={k}>
                <label className="flex items-start gap-3 text-sm">
                  <input type="checkbox" className="mt-1" checked={box.notify[k] ?? true} disabled={notify.isPending}
                    onChange={e => notify.mutate({ ...box.notify, sla: box.notify.sla ?? true, [k]: e.target.checked })} />
                  <span><span className="font-medium">{NOTIFY_LABEL[k][0]}</span><span className="block text-xs text-muted">{NOTIFY_LABEL[k][1]}</span></span>
                </label>
              </li>
            ))}
          </ul>
        </section>
      )}

      {box && data?.activity && data.activity.length > 0 && (
        <section className="rounded-lg border border-line bg-white p-5">
          <h2 className="text-base font-semibold">Actividad reciente del buzón</h2>
          <p className="mt-1 text-sm text-muted">Qué hizo Veta Desk con cada correo que llegó.</p>
          <div className="mt-3 overflow-x-auto rounded-md border border-line">
            <table className="w-full min-w-[640px] border-collapse text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted">
                  <th className="border-b border-line px-3 py-2 font-semibold">Cuándo</th>
                  <th className="border-b border-line px-3 py-2 font-semibold">De · Asunto</th>
                  <th className="border-b border-line px-3 py-2 font-semibold">Resultado</th>
                </tr>
              </thead>
              <tbody>
                {data.activity.map((a, i) => {
                  const [label, tone] = OUTCOME[a.outcome] ?? [a.outcome, 'text-muted'];
                  return (
                    <tr key={i} className="border-b border-[#EFEDE9] align-top last:border-0">
                      <td className="whitespace-nowrap px-3 py-2 text-xs text-muted" title={dateTime(a.at)}>{relativeTime(a.at)}</td>
                      <td className="px-3 py-2">
                        <div className="text-xs text-muted">{a.from}</div>
                        <div className="max-w-[420px] truncate" title={a.subject}>{a.subject || '(sin asunto)'}</div>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <span className={`font-semibold ${tone}`}>{label}</span>
                        {a.ticketNumber && <> · <Link to={`/tickets/${a.ticketNumber}`} className="font-mono text-accent underline-offset-2 hover:underline">{a.ticketNumber}</Link></>}
                        {a.error && <div className="mt-0.5 text-[#8F1D1D]">{a.error}</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {data && (
        <section className="rounded-lg border border-line bg-white p-5">
          <h2 className="text-base font-semibold">Dominios de correo por cliente</h2>
          <p className="mt-1 text-sm text-muted">
            Un correo nuevo de <span className="font-mono text-xs">alguien@pamhotels.com</span> crea un ticket de ese cliente. Si el dominio no está aquí,
            el correo queda en <strong className="font-semibold text-ink">Correos por clasificar</strong>.
          </p>
          <div className="mt-3 overflow-x-auto rounded-md border border-line">
            <table className="w-full min-w-[560px] border-collapse text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted">
                  <th className="border-b border-line px-3 py-2 font-semibold">Cliente</th>
                  <th className="border-b border-line px-3 py-2 font-semibold">Dominios</th>
                  <th className="border-b border-line px-3 py-2" />
                </tr>
              </thead>
              <tbody>{data.organizations.map(o => <DomainRow key={o.id} org={o} onSaved={refresh} />)}</tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
